// Typographic conventions that belong to the SCRIPT, not to one language.
//
// Three places asked `i18n.language === 'en'` and gave everything else the
// Arabic answer:
//
//   FormApproval.jsx        listSep = lang === 'en' ? ', ' : '، '
//   BrowseFormsPage.jsx     the same, on the profile detail panel
//   ScheduledCallLandingPage.jsx  name = lang === 'en' ? 'Wefaqna' : 'وفّقنا'
//
// So a French or German member read "Cairo، Egypt" with an Arabic comma, and
// the scheduled-call page offered them the Arabic wordmark. INV-I18N-1 again:
// a boolean cannot hold five languages, and the binary that looks harmless is
// the one where English is the special case instead of Arabic.
//
// The question these sites are really asking is "is this page in Arabic
// script?" — so that is the function, and it has one home. A sixth language
// costs nothing here.

/** The language's base code, e.g. 'es' from 'es-419'. */
export function baseLanguage(language) {
  return (language || 'ar').toLowerCase().split(/[-_]/)[0];
}

/**
 * Written in Arabic script: Arabic, and Urdu since 2026-10-23. Urdu takes the
 * Arabic comma and the Arabic wordmark too — it is the same alphabet, and
 * "وفّقنا" is how the name is written in it.
 */
export function isArabicScript(language) {
  return ['ar', 'ur'].includes(baseLanguage(language));
}

/**
 * The separator between items in a running list ("Cairo, Egypt").
 * Arabic uses U+060C ARABIC COMMA; every Latin-script language uses a comma.
 */
export function listSeparator(language) {
  return isArabicScript(language) ? '، ' : ', ';
}

/**
 * The brand name as it should appear in running text.
 *
 * Arabic keeps the wordmark; everything else uses the established Latin
 * spelling that the Play listing and the localized manifests already use.
 * Mirrors `siteName()` in SEO.jsx — if these two ever disagree, the share
 * card and the page body will disagree about what the product is called.
 */
export function brandName(language) {
  return isArabicScript(language) ? 'وفّقنا' : 'Wefaqna';
}

/**
 * Keep a phone number or address running its own way inside a sentence that
 * runs the other way.
 *
 * `+212634020387` interpolated into Arabic renders with its `+` on the wrong
 * side — the `+` is a bidi-neutral character and takes the direction of what
 * surrounds it — so the member reads a number that is not the one they typed.
 * That is worse than showing none, which is why it matters most on the reset
 * code screen, whose whole job is letting them spot a wrong digit (BUG-386).
 *
 * U+2066 LEFT-TO-RIGHT ISOLATE / U+2069 POP DIRECTIONAL ISOLATE are the Unicode
 * instrument for it. Applied unconditionally: they are invisible in every
 * Latin-script language, so there is no language test to get wrong, and the
 * mobile client's `_ltr()` does exactly this.
 */
export function isolateLtr(value) {
  return `\u2066${value ?? ''}\u2069`;
}
